import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './ManageCard.styles';
import {
  Button,
  IconSupport,
  Tooltip,
  Toggle,
  IconLoading,
} from 'gatorcomponents';
import PropTypes from 'prop-types';

export const ManageCard = ({
  cardTitle = 'card title',
  testId = 'manage-card',
  cardDescription = 'card description',
  tooltipLabel,
  button,
  toggle,
  icon,
  readMoreButtonLabel,
  blockedUntil = 0,
  blockedTimeTooltip,
  someFeatureIsLoading,
  isMobile,
}) => {
  const [readMore, setReadMore] = useState(false);
  const [blockedTime, setBlockedTime] = useState(0);

  const blockedToggle = !!blockedTime || (toggle && !!toggle.loading) || someFeatureIsLoading;

  const handleMarkToggle = () => {
    if (blockedToggle) return;
    toggle.onMark && toggle.onMark();
  };

  const setPreviousBlockedTime = useCallback(() => {
    const timestamp = blockedUntil;
    const currentTimestamp = Math.floor(Date.now() / 1000);

    if (timestamp > currentTimestamp) {
      setBlockedTime(timestamp - currentTimestamp);
    }
  }, [blockedUntil]);

  useEffect(() => {
    setPreviousBlockedTime();
  }, [setPreviousBlockedTime]);

  const updateBlockedTime = useCallback(() => {
    const secondsRemaining = blockedTime;

    if (secondsRemaining <= 0) return;

    const intervalId = setTimeout(() => {
      setBlockedTime(secondsRemaining - 1);
    }, 1000);

    return () => clearTimeout(intervalId);
  }, [blockedTime]);

  useEffect(() => {
    const cleanup = updateBlockedTime();
    return cleanup;
  }, [updateBlockedTime]);

  const mountCardDescription = () => {
    const needTruncateText = isMobile && cardDescription.length > 88 && readMore === false;

    if (needTruncateText) {
      return `${cardDescription.substring(0, 88)}...`;
    }
    return cardDescription;
  };

  return (
    <Styles.CardContainer data-testid={`${testId}-card`}>
      <Styles.CardHeadContainer>
        <Styles.IconContainer>
          {icon}
        </Styles.IconContainer>
        <Styles.CardTitle>
          {cardTitle}
          <Styles.TooltipIconContainer>
            {!!tooltipLabel && (
              <Tooltip content={tooltipLabel}>
                <IconSupport size="sm" color="primaryExtraDark" />
              </Tooltip>
            )}
          </Styles.TooltipIconContainer>
        </Styles.CardTitle>

        <Styles.ToggleContainer>
          {toggle && !!toggle.loading && <IconLoading />}
          {!!toggle && (
            <Tooltip
              content={blockedTimeTooltip({ seconds: blockedTime, isActive: toggle.checked })}
              disabled={!blockedTime}
              preferredPosition="topLeft"
            >
              <Styles.ToggleLabel onClick={handleMarkToggle}>
                <Toggle
                  testId={`${testId}-toggle`}
                  checked={toggle.checked}
                  disabled={blockedToggle}
                  controlledByFather
                />
              </Styles.ToggleLabel>
            </Tooltip>
          )}
        </Styles.ToggleContainer>
      </Styles.CardHeadContainer>

      <Styles.CardDescription extraPadding={!!button}>
        {`${mountCardDescription()} `}
        {isMobile && (
          <Button
            testId={`${testId}-read-more-button`}
            label={readMoreButtonLabel(readMore)}
            variant="text"
            onClick={() => setReadMore(!readMore)}
          />
        )}
      </Styles.CardDescription>

      {!!button && (
        <Styles.ButtonContainer>
          <Tooltip content={blockedTimeTooltip({ seconds: blockedTime })} disabled={!blockedTime} preferredPosition="topLeft">
            <Button
              label={button.buttonLabel}
              onClick={button.onClick}
              width="fit"
              testId={`${testId}-button`}
              disabled={!!blockedTime}
            />
          </Tooltip>
        </Styles.ButtonContainer>
      )}
    </Styles.CardContainer>
  );
};

ManageCard.propTypes = {
  cardTitle: PropTypes.string,
  testId: PropTypes.string,
  cardDescription: PropTypes.string,
  tooltipLabel: PropTypes.any,
  button: PropTypes.shape({
    buttonLabel: PropTypes.string,
    onClick: PropTypes.func,
  }),
  toggle: PropTypes.shape({
    checked: PropTypes.bool,
    onMark: PropTypes.func,
    loading: PropTypes.bool,
  }),
  icon: PropTypes.node,
  readMoreButtonLabel: PropTypes.func,
  blockedUntil: PropTypes.number,
  blockedTimeTooltip: PropTypes.func,
  someFeatureIsLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export default ManageCard;
